import type { MouseEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@crm/shared/lib';
import { Tooltip, TooltipTrigger, TooltipContent } from '@crm/shared/ui';

import type { TDealTasksData } from '@crm/entities/deal';

type TTaskStatus = NonNullable<TDealTasksData['tasks'][number]['context']['status']>;

interface TaskStatusSwitcherProps {
    status?: TTaskStatus;
    classes?: string;
    disabled?: boolean;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const TaskStatusSwitcher = ({
    classes,
    disabled = false,
    onClick,
    status = 'is_waiting',
}: TaskStatusSwitcherProps) => {
    const { t } = useTranslation();

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={cn(
                'flex size-5 shrink-0 cursor-pointer items-center justify-center rounded bg-smtr-main-accent-50 transition-colors disabled:pointer-events-none disabled:opacity-60',
                status === 'is_waiting' && 'bg-gray-100',
                classes
            )}
        >
            {status === 'in_progress' ? (
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="transparent"
                            >
                                <g clipPath="url(#crm_task_status_in_progress)">
                                    <path
                                        d="M7 14C3.14008 14 0 10.8599 0 7C0 3.14008 3.14008 0 7 0C10.8599 0 14 3.14008 14 7C14 10.8599 10.8599 14 7 14ZM7 1.16667C3.7835 1.16667 1.16667 3.7835 1.16667 7C1.16667 10.2165 3.7835 12.8333 7 12.8333C10.2165 12.8333 12.8333 10.2165 12.8333 7C12.8333 3.7835 10.2165 1.16667 7 1.16667Z"
                                        fill="#1094F7"
                                    />
                                    <path
                                        d="M7 2C7.84598 2 8.67812 2.21465 9.41855 2.62386C10.159 3.03307 10.7835 3.62345 11.2336 4.33975C11.6837 5.05605 11.9447 5.87485 11.9921 6.71949C12.0396 7.56414 11.872 8.40702 11.5049 9.16923C11.1379 9.93145 10.5835 10.5881 9.89357 11.0777C9.20365 11.5672 8.40078 11.8738 7.56013 11.9685C6.71948 12.0633 5.86852 11.9432 5.08692 11.6195C4.30531 11.2959 3.61861 10.7791 3.0911 10.1178L7 7L7 2Z"
                                        fill="#1094F7"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="crm_task_status_in_progress">
                                        <rect width="14" height="14" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        }
                    />
                    <TooltipContent>
                        {t('crm.dealDetailsContent.tasksBlock.status.inProgress')}
                    </TooltipContent>
                </Tooltip>
            ) : (
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                            >
                                <g clipPath="url(#crm_task_status_is_waiting)">
                                    <path
                                        d="M7 14C3.14008 14 0 10.8599 0 7C0 3.14008 3.14008 0 7 0C10.8599 0 14 3.14008 14 7C14 10.8599 10.8599 14 7 14ZM7 1.16667C3.7835 1.16667 1.16667 3.7835 1.16667 7C1.16667 10.2165 3.7835 12.8333 7 12.8333C10.2165 12.8333 12.8333 10.2165 12.8333 7C12.8333 3.7835 10.2165 1.16667 7 1.16667ZM8.98158 9.21842L7.58333 6.84075V2.91667H6.41667V7.15867L7.97592 9.80933L8.98217 9.21783L8.98158 9.21842Z"
                                        fill="#757575"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="crm_task_status_is_waiting">
                                        <rect width="14" height="14" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        }
                    />
                    <TooltipContent>
                        {t('crm.dealDetailsContent.tasksBlock.status.waiting')}
                    </TooltipContent>
                </Tooltip>
            )}
        </button>
    );
};
